<script setup>
// SiteUP CRM (Módulo Imobiliária): "Gerar ficha do lead" do menu do Resolver
// (roadmap ficha-lead; mockup aprovado ficha-lead.html, telas 2 a 4).
// Estados: gerando, pronta (travada, Imprimir é o principal), editando (F5:
// Editar libera os campos, Concluir trava de novo), erro e impressão. A
// impressão é uma página A4 por cima do app; o @media print esconde o resto e
// o navegador faz o Imprimir e o Salvar em PDF (F3, sem biblioteca de PDF).
// Nada do que se edita aqui volta para o contato: a ficha é só para imprimir.
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ImoveisAPI from 'dashboard/api/imoveis';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LeadSheetDocument from './LeadSheetDocument.vue';
import { toEditableSheet } from './leadSheet';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const PRINTING_CLASS = 'lead-sheet-printing';

const { t, locale } = useI18n();

const dialogRef = ref(null);
// loading | ready | editing | error | print
const state = ref('loading');
const sheet = ref(null);
let requestSeq = 0;

const isSheetVisible = computed(() =>
  ['ready', 'editing'].includes(state.value)
);

const generate = async () => {
  requestSeq += 1;
  const seq = requestSeq;
  state.value = 'loading';
  try {
    const { data } = await ImoveisAPI.generateLeadSheet(props.conversationId);
    if (seq !== requestSeq) return;
    sheet.value = toEditableSheet(data, { locale: locale.value, t });
    state.value = 'ready';
  } catch {
    if (seq !== requestSeq) return;
    state.value = 'error';
  }
};

const setPrinting = on => {
  document.body.classList.toggle(PRINTING_CLASS, on);
};

const open = () => {
  sheet.value = null;
  setPrinting(false);
  dialogRef.value?.open();
  generate();
};

const close = () => {
  dialogRef.value?.close();
};

// O Dialog avisa quando fecha (X, Esc, clique fora). Indo para a impressão ele
// também fecha, mas aí a ficha continua viva por baixo.
const onDialogClose = () => {
  if (state.value === 'print') return;
  requestSeq += 1;
  state.value = 'loading';
  sheet.value = null;
};

const edit = () => {
  state.value = 'editing';
};

const done = () => {
  state.value = 'ready';
};

const updateField = ({ section, key, value }) => {
  if (!sheet.value) return;
  if (section) sheet.value[section][key] = value;
  else sheet.value[key] = value;
};

const openPrint = () => {
  state.value = 'print';
  setPrinting(true);
  dialogRef.value?.close();
};

const backFromPrint = () => {
  setPrinting(false);
  state.value = 'ready';
  dialogRef.value?.open();
};

const printNow = () => {
  window.print();
};

onBeforeUnmount(() => setPrinting(false));

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    content-tag="div"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="onDialogClose"
  >
    <div class="flex flex-col gap-4" data-testid="lead-sheet-modal">
      <div class="flex items-center gap-2.5">
        <span
          class="i-ph-clipboard-text size-5 text-n-slate-12"
          aria-hidden="true"
        />
        <h2 class="flex-1 text-[17px] font-bold text-n-slate-12">
          {{ t('CONVERSATION.LEAD_SHEET.TITLE') }}
        </h2>
        <Button
          icon="i-ph-x"
          variant="ghost"
          color="slate"
          size="sm"
          :aria-label="t('CONVERSATION.LEAD_SHEET.CLOSE')"
          @click="close"
        />
      </div>

      <!-- Como no mockup, só o miolo rola: o título com o X e a barra com
           Imprimir ficam sempre à vista, e a página atrás não rola. -->
      <div
        class="-mx-1 flex min-h-0 max-h-[calc(100dvh-16rem)] flex-col gap-4 overflow-y-auto px-1"
        data-testid="lead-sheet-body"
      >
        <div
          v-if="state === 'loading'"
          class="flex flex-col gap-2.5 py-2"
          data-testid="lead-sheet-generating"
          aria-live="polite"
        >
          <ul class="flex flex-col gap-1.5 text-[13px] text-n-slate-12">
            <li
              v-for="step in ['CONTACT', 'MESSAGES', 'PROPERTIES']"
              :key="step"
              class="flex items-center gap-2"
            >
              <span class="i-ph-check size-[18px] text-n-teal-10" />
              {{ t(`CONVERSATION.LEAD_SHEET.GENERATING.${step}`) }}
            </li>
            <li class="flex items-center gap-2 text-n-slate-11">
              <span class="i-ph-clock size-[18px]" />
              {{ t('CONVERSATION.LEAD_SHEET.GENERATING.BUILDING') }}
            </li>
          </ul>
          <div
            v-for="width in ['60%', '100%', '80%', '70%']"
            :key="width"
            class="h-3 rounded-md bg-n-alpha-2 animate-pulse"
            :style="{ width }"
          />
        </div>

        <div
          v-else-if="state === 'error'"
          class="flex flex-col items-start gap-3 py-2"
          data-testid="lead-sheet-error"
          role="alert"
        >
          <p class="text-sm text-n-slate-12">
            {{ t('CONVERSATION.LEAD_SHEET.ERROR') }}
          </p>
          <Button
            :label="t('CONVERSATION.LEAD_SHEET.TRY_AGAIN')"
            icon="i-ph-arrow-clockwise"
            variant="outline"
            color="slate"
            size="sm"
            @click="generate"
          />
        </div>

        <template v-else-if="isSheetVisible && sheet">
          <p
            v-if="state === 'editing'"
            class="flex items-start gap-2 text-[13px] text-n-slate-11"
            data-testid="lead-sheet-edit-hint"
          >
            <span class="i-ph-pencil-simple size-[18px] shrink-0" />
            {{ t('CONVERSATION.LEAD_SHEET.EDIT_HINT') }}
          </p>
          <p
            v-if="sheet.partial"
            class="flex items-start gap-2 text-[13px] text-n-amber-11"
            data-testid="lead-sheet-partial"
          >
            <span class="i-ph-warning size-[18px] shrink-0" />
            {{ t('CONVERSATION.LEAD_SHEET.PARTIAL') }}
          </p>
          <LeadSheetDocument
            :sheet="sheet"
            :editable="state === 'editing'"
            @update-field="updateField"
          />
        </template>
      </div>
    </div>

    <template #footer>
      <div
        v-if="isSheetVisible"
        class="flex flex-wrap items-center gap-2 border-t border-n-weak pt-3"
        data-testid="lead-sheet-actions"
      >
        <Button
          v-if="state === 'ready'"
          :label="t('CONVERSATION.LEAD_SHEET.EDIT')"
          icon="i-ph-pencil-simple"
          variant="outline"
          color="slate"
          size="sm"
          data-testid="lead-sheet-edit"
          @click="edit"
        />
        <Button
          v-else
          :label="t('CONVERSATION.LEAD_SHEET.DONE')"
          icon="i-ph-check"
          variant="outline"
          color="slate"
          size="sm"
          data-testid="lead-sheet-done"
          @click="done"
        />
        <Button
          :label="t('CONVERSATION.LEAD_SHEET.REGENERATE')"
          icon="i-ph-arrow-clockwise"
          variant="outline"
          color="slate"
          size="sm"
          data-testid="lead-sheet-regenerate"
          @click="generate"
        />
        <span class="flex-1" />
        <Button
          :label="t('CONVERSATION.LEAD_SHEET.PRINT')"
          icon="i-ph-printer"
          color="slate"
          size="sm"
          class="!bg-n-slate-12 !text-n-background hover:enabled:!bg-n-slate-11"
          data-testid="lead-sheet-print"
          @click="openPrint"
        />
      </div>
    </template>
  </Dialog>

  <Teleport to="body">
    <div
      v-if="state === 'print' && sheet"
      class="lead-sheet-print"
      data-testid="lead-sheet-print-view"
    >
      <div class="lead-sheet-print__bar">
        <button
          type="button"
          class="lead-sheet-print__btn"
          data-testid="lead-sheet-print-back"
          @click="backFromPrint"
        >
          <span class="i-ph-caret-left size-[18px]" aria-hidden="true" />
          {{ t('CONVERSATION.LEAD_SHEET.PRINT_PAGE.BACK') }}
        </button>
        <span class="flex-1" />
        <button
          type="button"
          class="lead-sheet-print__btn lead-sheet-print__btn--primary"
          data-testid="lead-sheet-print-now"
          @click="printNow"
        >
          <span class="i-ph-printer size-[18px]" aria-hidden="true" />
          {{ t('CONVERSATION.LEAD_SHEET.PRINT_PAGE.PRINT') }}
        </button>
      </div>
      <div class="lead-sheet-print__a4">
        <LeadSheetDocument :sheet="sheet" print />
      </div>
    </div>
  </Teleport>
</template>

<style>
/* Página de impressão: fundo cinza de mesa e a folha A4 no meio, como uma
   prévia. Cores fixas porque é papel. */
.lead-sheet-print {
  position: fixed;
  inset: 0;
  z-index: 10000;
  overflow-y: auto;
  background: #e9ecef;
  padding: 24px 16px;
}
.lead-sheet-print__bar {
  max-width: 794px;
  margin: 0 auto 14px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.lead-sheet-print__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  padding: 9px 14px;
  border-radius: 12px;
  border: 1px solid rgb(var(--border-strong));
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-12));
}
.lead-sheet-print__btn--primary {
  background: rgb(var(--blue-9));
  border-color: rgb(var(--blue-9));
  color: #fff;
}
.lead-sheet-print__a4 {
  max-width: 794px;
  margin: 0 auto;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.12);
}

@page {
  size: A4;
  margin: 14mm;
}

/* Na impressão só a ficha existe: o app, os modais e a barra somem. */
@media print {
  body.lead-sheet-printing > *:not(.lead-sheet-print) {
    display: none !important;
  }
  body.lead-sheet-printing {
    background: #fff !important;
  }
  .lead-sheet-print {
    position: static;
    overflow: visible;
    background: #fff;
    padding: 0;
  }
  .lead-sheet-print__bar {
    display: none;
  }
  .lead-sheet-print__a4 {
    box-shadow: none;
    max-width: none;
  }
  .lead-sheet-print .lead-sheet-paper--print {
    padding: 0;
    min-height: 0;
  }
}
</style>
